import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAppUrl, getPublicApiBase } from '../utils/urlHelper';
import Vapi from '@vapi-ai/web';
import DemoBookingModal from './DemoBookingModal';

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="overflow-hidden transition-all duration-200"
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <button
        className="w-full py-5 text-left flex items-center justify-between gap-4 group"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-white font-medium text-base group-hover:text-purple-300 transition-colors">{question}</span>
        <svg
          className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-purple-400' : 'text-gray-500'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="pb-5">
          <p style={{ color: 'rgba(255,255,255,0.55)' }} className="leading-relaxed text-sm">{answer}</p>
        </div>
      )}
    </div>
  );
};

// Vapi Configuration
const VAPI_PUBLIC_KEY = '1982777e-4159-4b67-981d-4a99ae5faf31'; // Updated public key with all assistants permission
const VAPI_ASSISTANT_ID = '25a2f2c6-d260-4b73-8aa8-9eb6fc481e78'; // Appy AI Assistant ID
const REST_API_KEY = process.env.REACT_APP_VAPI_API_KEY;

/** Pricing cards — blocks drive section headings + lists; cta.action: register | demo */
const tiers = [
  {
    name: 'Free Demo',
    price: 'Free',
    setupLines: [],
    popular: false,
    borderClass: 'border-gray-700',
    blocks: [
      {
        type: 'list',
        items: [
          'Build and customise your AI voice agent',
          'Test real conversations instantly',
          'Experience how your business sounds on AI',
          'No calls, no integrations, no commitment',
        ],
      },
      {
        type: 'tagline',
        text: 'Explore before you go live',
      },
    ],
    cta: { label: 'Get Started Free', action: 'register' },
  },
  {
    name: 'Growth Plan',
    price: 'From $249/month',
    setupLines: ['Setup: From $199 (once-off)'],
    popular: true,
    borderClass: 'border-2 border-purple-500',
    blocks: [
      {
        type: 'section',
        title: '🚀 What you get:',
        items: [
          '24/7 AI call answering — never miss a call again',
          'Handles inbound & outbound calls automatically',
          'Routes calls to multiple team members or departments',
          'Lead qualification — capture and filter high-quality leads',
          'Appointment booking (Google / Outlook integration)',
          'Instant email notifications & follow-ups',
          'Customisable voice, tone, and personality',
          'Real-time dashboard with call logs & insights',
        ],
      },
      {
        type: 'section',
        title: '💡 Why businesses choose this:',
        items: [
          'Works while you sleep — no downtime, ever',
          'Never takes leave, breaks, or sick days',
          'Reduces admin workload instantly',
          'Saves significantly vs hiring full-time staff for calls',
        ],
      },
      {
        type: 'section',
        title: '💰 Usage:',
        items: ['From $0.18 – $0.25 per minute'],
      },
      {
        type: 'tagline',
        text: 'Perfect for businesses augmenting or replacing manual phone coverage with an AI assistant',
      },
    ],
    cta: { label: 'Request Setup', action: 'demo' },
  },
  {
    name: 'Pro / Business',
    price: 'From $649/month',
    setupLines: ['Setup: From $399 (once-off)'],
    popular: false,
    borderClass: 'border-gray-700',
    blocks: [
      {
        type: 'section',
        title: '🚀 Everything in Growth, plus:',
        items: [
          'Advanced call routing (departments, priority routing, logic-based flows)',
          'CRM integrations (HubSpot, Salesforce, Zoho, etc.)',
          'SMS automation & follow-ups',
          'Custom AI workflows (sales, support, order handling)',
          'Lead qualification + intelligent routing to the right person',
          'Multi-number setup for different teams or locations',
          'Priority setup & faster deployment',
          'Enhanced analytics & performance tracking',
        ],
      },
      {
        type: 'section',
        title: '💡 Built for scale:',
        items: [
          'Handles high call volumes effortlessly',
          'Optimised for sales teams and growing businesses',
          'Automates repetitive workflows across your business',
          'Improves response time and customer experience',
        ],
      },
      {
        type: 'section',
        title: '💰 Usage:',
        items: ['From $0.12 – $0.18 per minute'],
      },
      {
        type: 'tagline',
        text: 'Ideal for scaling teams and high-growth businesses',
      },
    ],
    cta: { label: 'Speak to an Expert', action: 'demo' },
  },
];

export default function MarketingPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [callStatus, setCallStatus] = useState('idle'); // 'idle', 'starting', 'in-call'
  const [vapiClient, setVapiClient] = useState(null);
  const [vapiLoaded, setVapiLoaded] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm the Appify AI assistant. I can help you learn about our Voice AI solutions, pricing, features, or book a demo. What would you like to know?",
      isAI: true
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  const vapiRef = useRef(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Initialize Vapi SDK
  useEffect(() => {
    const initVapi = () => {
      console.log('🔍 Initializing Vapi SDK...');
      
      try {
        console.log('✅ Vapi SDK found, creating client...');
        const client = new Vapi(VAPI_PUBLIC_KEY);
        
        // Set up event listeners
        client.on('call-start', () => {
          console.log('📞 Call started');
          setCallStatus('in-call');
        });
        
        client.on('call-end', () => {
          console.log('📞 Call ended');
          setCallStatus('idle');
        });
        
        client.on('call-error', (error) => {
          console.error('❌ Call error:', error);
          setCallStatus('idle');
        });
        
        setVapiClient(client);
        vapiRef.current = client;
        setVapiLoaded(true);
        console.log('✅ Vapi client initialized successfully');
      } catch (error) {
        console.error('❌ Error creating Vapi client:', error);
        setVapiLoaded(true); // Set to true even if there's an error to show the button
      }
    };

    // Initialize immediately
    initVapi();
  }, []);

  // Start the call
  const startCall = () => {
    if (callStatus !== 'idle') return;
    
    console.log('🎤 Starting call with assistant:', VAPI_ASSISTANT_ID);
    setCallStatus('starting');
    
    if (!vapiClient) {
      console.error('❌ Vapi client not available');
      setCallStatus('idle');
      alert('Voice call not available. Vapi SDK failed to load. Please refresh the page and try again.');
      return;
    }
    
    try {
      if (typeof vapiClient.start === 'function') {
        vapiClient.start(VAPI_ASSISTANT_ID);
        console.log('✅ Call started successfully');
        
        // Set a timeout to show error if call doesn't start
        setTimeout(() => {
          if (callStatus === 'starting') {
            console.error('❌ Call timeout - no response from Vapi');
            setCallStatus('idle');
            alert('Call failed to start. Please check your microphone permissions and try again.');
          }
        }, 10000); // 10 second timeout
      } else {
        console.error('❌ start function not available');
        setCallStatus('idle');
        alert('Voice call not available. Vapi SDK not properly loaded.');
      }
    } catch (error) {
      console.error('❌ Error starting call:', error);
      setCallStatus('idle');
      alert('Failed to start call. Please check your microphone permissions and try again.');
    }
  };

  // End the call
  const endCall = () => {
    if (callStatus !== 'in-call') return;
    
    console.log('📞 Ending call');
    try {
      // Try different methods to end the call
      if (vapiClient) {
        if (typeof vapiClient.stop === 'function') {
          console.log('🛑 Using stop() method');
          vapiClient.stop();
        } else if (typeof vapiClient.hangup === 'function') {
          console.log('🛑 Using hangup() method');
        vapiClient.hangup();
        } else if (typeof vapiClient.end === 'function') {
          console.log('🛑 Using end() method');
          vapiClient.end();
        } else {
          console.error('❌ No end call method available');
          setCallStatus('idle');
        }
      } else if (vapiRef.current) {
        if (typeof vapiRef.current.stop === 'function') {
          console.log('🛑 Using stop() method (ref)');
          vapiRef.current.stop();
        } else if (typeof vapiRef.current.hangup === 'function') {
          console.log('🛑 Using hangup() method (ref)');
        vapiRef.current.hangup();
        } else if (typeof vapiRef.current.end === 'function') {
          console.log('🛑 Using end() method (ref)');
          vapiRef.current.end();
        } else {
          console.error('❌ No end call method available (ref)');
          setCallStatus('idle');
        }
      } else {
        console.error('❌ No Vapi client available');
        setCallStatus('idle');
      }
      
      // Fallback: Force status reset after 2 seconds if Vapi event doesn't fire
      setTimeout(() => {
        if (callStatus === 'in-call') {
          console.log('⚠️ Forcing call status reset - Vapi event may not have fired');
          setCallStatus('idle');
        }
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error ending call:', error);
      setCallStatus('idle');
    }
  };

  // Toggle call (start or end)
  const toggleCall = () => {
    console.log('🔄 Toggle call called, current status:', callStatus);
    
    if (callStatus === 'idle') {
      console.log('🚀 Starting call...');
      startCall();
    } else if (callStatus === 'in-call') {
      console.log('🛑 Ending call...');
      endCall();
    } else {
      console.log('⚠️ Call in progress, ignoring click');
    }
  };

  // Handle chat input
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = {
      id: Date.now(),
      text: chatInput,
      isAI: false
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setChatLoading(true);

    try {
      // Call Vapi Chat API with the same assistant used for voice calls
      const response = await fetch('https://api.vapi.ai/chat', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${REST_API_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          input: currentInput,
          assistantId: VAPI_ASSISTANT_ID,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Extract the AI response from Vapi
      let aiResponse = '';
      if (data.output && data.output.length > 0) {
        // Get the last message from the output
        const lastMessage = data.output[data.output.length - 1];
        aiResponse = lastMessage.message || lastMessage.content || 'I received your message but couldn\'t generate a response.';
      } else if (data.message) {
        aiResponse = data.message;
      } else {
        aiResponse = 'I received your message but couldn\'t generate a response.';
      }

      const responseMessage = {
        id: Date.now() + 1,
        text: aiResponse,
        isAI: true
      };
      setChatMessages(prev => [...prev, responseMessage]);
      
    } catch (error) {
      console.error('Vapi Chat API error:', error);
      const errorResponse = {
        id: Date.now() + 1,
        text: "I'm having trouble connecting to my AI brain right now. Please try again or contact us directly at bernie@appifyai.com",
        isAI: true
      };
      setChatMessages(prev => [...prev, errorResponse]);
    } finally {
      setChatLoading(false);
    }
  };

  const demoMailTo = (subjectSuffix = 'Book a Demo') => {
    const subject = encodeURIComponent(`AppifyAI — ${subjectSuffix}`);
    const body = encodeURIComponent(
      `Hi AppifyAI Team,%0D%0A%0D%0A` +
      `I would like to book a demo.%0D%0A%0D%0A` +
      (name ? `Name: ${name}%0D%0A` : '') +
      (email ? `Email: ${email}%0D%0A` : '') +
      (message ? `Message: ${message}%0D%0A` : '') +
      `%0D%0AThank you!`
    );
    return `mailto:bernie@appifyai.com?subject=${subject}&body=${body}`;
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactError('');
    setContactSuccess(false);

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedMessage = message.trim();
    if (!trimmedName || !trimmedEmail || !trimmedMessage) {
      setContactError('Please fill in your name, email, and message.');
      return;
    }

    setContactSubmitting(true);
    try {
      const base = getPublicApiBase();
      const response = await fetch(`${base}/api/onboarding/notify-marketing-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          email: trimmedEmail,
          message: trimmedMessage,
        }),
      });
      const result = await response.json().catch(() => ({}));

      if (response.ok && (result.success || result.ok)) {
        setName('');
        setEmail('');
        setMessage('');
        setContactSuccess(true);
        setContactSubmitting(false);
        return;
      }

      throw new Error(result.error || 'Failed to send');
    } catch (err) {
      console.error('Contact form:', err);
      setContactSubmitting(false);
      window.location.href = demoMailTo('Contact Form Submission');
      setContactSuccess(true);
      setContactError(
        'We could not reach the server, so your email app was opened instead. If nothing opened, use Book a Demo or email us directly.'
      );
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#07070f' }}>
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden" style={{ backgroundColor: '#0a0a0f' }}>
        {/* Keyframe animations injected inline */}
        <style>{`
          @keyframes floatBounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-18px); }
          }
          @keyframes orbPulse {
            0%, 100% { opacity: 0.35; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.12); }
          }
          @keyframes orbPulse2 {
            0%, 100% { opacity: 0.2; transform: scale(1); }
            50% { opacity: 0.45; transform: scale(1.08); }
          }
          .appy-float { animation: floatBounce 3.8s ease-in-out infinite; }
          .orb-pulse { animation: orbPulse 5s ease-in-out infinite; }
          .orb-pulse2 { animation: orbPulse2 7s ease-in-out infinite; }
        `}</style>

        {/* Subtle noise/grid texture */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(139,92,246,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.03) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>

        {/* Deep purple ambient light top-left */}
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)'
        }}></div>

        <div className="relative z-10">
        {/* Navigation Bar */}
        <header className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center py-4 md:py-6 px-4 md:px-10">
          <div className="flex items-center gap-3 flex-shrink-0">
            <img
              src="/screenshots/appymascot.png.png"
              alt="Appy - The Appify AI Mascot"
              className="w-10 h-10 md:w-12 md:h-12 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="hidden w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
              Appy
            </div>
            <span className="text-xl md:text-2xl font-bold text-white tracking-tight">Appify.AI</span>
          </div>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-400 hover:text-white transition text-sm font-medium tracking-wide">Features</a>
            <a href="#pricing" className="text-gray-400 hover:text-white transition text-sm font-medium tracking-wide">Pricing</a>
            <a href="#contact" className="text-gray-400 hover:text-white transition text-sm font-medium tracking-wide">Contact</a>
            <a href={getAppUrl('/login')} className="px-5 py-2 rounded-lg text-sm font-semibold text-white border border-purple-500/50 hover:border-purple-400 hover:bg-purple-500/10 transition">
              Log in
            </a>
          </nav>
          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </header>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-10 pt-20 px-4 backdrop-blur-md" style={{ backgroundColor: 'rgba(10,10,15,0.97)' }}>
            <nav className="flex flex-col space-y-4">
              <a href="#features" className="text-gray-300 hover:text-white text-lg py-2 transition" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#pricing" className="text-gray-300 hover:text-white text-lg py-2 transition" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <a href="#contact" className="text-gray-300 hover:text-white text-lg py-2 transition" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <a href={getAppUrl('/login')} className="mt-4 inline-block px-6 py-3 text-center rounded-lg text-white font-semibold border border-purple-500/50 hover:bg-purple-500/10 transition" onClick={() => setMobileMenuOpen(false)}>
                Log in
              </a>
            </nav>
          </div>
        )}

        {/* Hero — two-column layout */}
        <main className="min-h-screen flex items-center px-4 md:px-10 pt-24 pb-16">
          <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">

            {/* LEFT COLUMN — copy + CTAs + stats */}
            <div className="flex flex-col items-start">
              {/* Eyebrow tag */}
              <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold tracking-widest uppercase" style={{ borderColor: 'rgba(139,92,246,0.35)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                Powered by Voice AI
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-[1.08] tracking-tight">
                Automate Your Business with{' '}
                <span style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #C084FC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  Intelligent Voice AI
                </span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-400 leading-relaxed max-w-lg">
                We build custom voice AI agents to answer calls, book appointments, and qualify leads — so you can focus on growing your business.
              </p>

              {/* CTAs */}
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <a
                  href={getAppUrl('/register')}
                  className="px-8 py-4 text-base font-semibold text-white rounded-xl transition transform hover:scale-105 hover:shadow-purple-500/30 hover:shadow-xl text-center"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #C084FC 100%)' }}
                >
                  Create Your AI Voice Agent Demo
                </a>
                <button
                  onClick={() => setShowDemoModal(true)}
                  className="px-8 py-4 text-base font-semibold text-white rounded-xl border transition hover:bg-white/5 hover:scale-105"
                  style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                >
                  Book A Demo
                </button>
              </div>

              {/* No card needed */}
              <div className="mt-5 flex items-center gap-2 text-sm text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>No Credit or Debit Card Required</span>
              </div>

              {/* Floating stats pills */}
              <div className="mt-10 flex flex-wrap gap-3">
                {[
                  { label: '500+ Businesses' },
                  { label: '1M+ Calls Handled' },
                  { label: '99.9% Uptime' },
                ].map(({ label }) => (
                  <span
                    key={label}
                    className="px-4 py-1.5 rounded-full text-sm font-medium text-purple-300"
                    style={{ backgroundColor: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.25)' }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN — Appy with glow */}
            <div className="flex items-center justify-center relative">
              {/* Outer ambient glow */}
              <div className="absolute w-80 h-80 rounded-full orb-pulse pointer-events-none" style={{
                background: 'radial-gradient(circle, rgba(139,92,246,0.35) 0%, rgba(192,132,252,0.12) 50%, transparent 75%)',
                filter: 'blur(40px)'
              }}></div>
              {/* Inner sharper glow */}
              <div className="absolute w-52 h-52 rounded-full orb-pulse2 pointer-events-none" style={{
                background: 'radial-gradient(circle, rgba(192,132,252,0.25) 0%, transparent 70%)',
                filter: 'blur(20px)'
              }}></div>

              {/* Appy mascot */}
              <div className="relative z-10 appy-float">
                <img
                  src="/screenshots/appymascot.png.png"
                  alt="Appy - The Appify AI Mascot"
                  className="w-64 h-64 md:w-80 md:h-80 object-contain drop-shadow-2xl"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-64 h-64 md:w-80 md:h-80 rounded-full items-center justify-center text-white text-4xl font-bold" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                  Appy
                </div>
              </div>
            </div>

          </div>
        </main>

          {/* Voice Agent Section - Below Hero */}
          <div className="mt-0 py-24 md:py-32 px-4 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-3xl mx-auto">
              {/* Section label */}
              <div className="mb-6 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block animate-pulse"></span>
                Live Voice Demo
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Speak to Appy Live</h2>
              <p className="mt-4 text-base md:text-lg max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Start a live voice conversation with our AI assistant. Ask anything about our services.
              </p>

              {/* Appy + mic layout */}
              <div className="mt-14 flex flex-col items-center gap-8">
                {/* Appy with glow */}
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-56 h-56 rounded-full pointer-events-none animate-orb-pulse" style={{
                    background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, rgba(192,132,252,0.1) 50%, transparent 75%)',
                    filter: 'blur(32px)',
                  }}></div>
                  <div className="relative z-10 animate-float">
                    <img
                      src="/screenshots/appymascot.png.png"
                      alt="Appy - The Appify AI Mascot"
                      className="w-44 h-44 object-contain drop-shadow-2xl"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-44 h-44 rounded-full items-center justify-center text-white text-3xl font-bold" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                      Appy
                    </div>
                  </div>
                </div>

                {/* Mic button */}
                <div className="relative flex items-center justify-center w-28 h-28">
                  {callStatus === 'in-call' && (
                    <>
                      <div className="absolute inset-0 rounded-full border border-purple-500/40 animate-ring-expand"></div>
                      <div className="absolute inset-0 rounded-full border border-purple-500/25 animate-ring-expand" style={{ animationDelay: '0.7s' }}></div>
                    </>
                  )}
                  <button
                    onClick={toggleCall}
                    disabled={callStatus === 'starting' || !vapiLoaded}
                    className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                      callStatus === 'in-call'
                        ? 'bg-red-500/20 border border-red-400/60 hover:bg-red-500/30'
                        : callStatus === 'starting'
                        ? 'border animate-pulse'
                        : 'hover:scale-105'
                    }`}
                    style={
                      callStatus === 'in-call' ? {} :
                      callStatus === 'starting' ? { borderColor: 'rgba(250,204,21,0.5)', backgroundColor: 'rgba(250,204,21,0.1)' } :
                      { background: 'linear-gradient(135deg, #8B5CF6, #C084FC)', boxShadow: '0 0 32px rgba(139,92,246,0.35)' }
                    }
                  >
                    {callStatus === 'in-call' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-9 w-9 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 10h6v6H9z" />
                      </svg>
                    ) : callStatus === 'starting' ? (
                      <div className="w-8 h-8 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin"></div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-9 w-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Status */}
                <p className={`text-sm font-medium transition-all duration-300 ${
                  !vapiLoaded ? 'text-yellow-400' :
                  callStatus === 'idle' ? 'text-purple-300' :
                  callStatus === 'starting' ? 'text-yellow-400' :
                  callStatus === 'in-call' ? 'text-red-300' : 'text-gray-400'
                }`}>
                  {!vapiLoaded && 'Loading voice SDK…'}
                  {vapiLoaded && callStatus === 'idle' && 'Tap the mic to start your free demo'}
                  {callStatus === 'starting' && 'Connecting…'}
                  {callStatus === 'in-call' && 'Call active — speak now'}
                </p>

                {!vapiLoaded && (
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce"></div>
                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rest of page */}
      <div className="relative" style={{ backgroundColor: '#07070f' }}>
        {/* Subtle purple ambient — top and mid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }}></div>
          <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.05) 0%, transparent 70%)', filter: 'blur(80px)' }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
        {/* AI Agent Chat Section */}
        <section id="ai-agent" className="py-16 md:py-24 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
              Chat with Appy
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Or Type Your Question</h2>
            <p className="mt-4 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Appify's AI agent answers questions about features, pricing, and getting started — 24/7.</p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="rounded-2xl flex flex-col h-96 overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
              <div className="p-5 flex-grow overflow-y-auto space-y-4">
                {chatMessages.map((msg) => (
                  <div key={msg.id} className={`flex items-end gap-2.5 ${msg.isAI ? 'justify-start' : 'justify-end'}`}>
                    {msg.isAI && (
                      <div className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>A</div>
                    )}
                    <div className={`max-w-xs px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      msg.isAI
                        ? 'text-white/80 rounded-bl-sm'
                        : 'text-white rounded-br-sm'
                    }`} style={msg.isAI
                      ? { backgroundColor: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.08)' }
                      : { background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }
                    }>
                      {msg.text}
                    </div>
                    {!msg.isAI && (
                      <div className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.15)' }}>U</div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex items-end gap-2.5 justify-start">
                    <div className="flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>A</div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-sm" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"></div>
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={handleChatSubmit} className="p-4 flex items-center gap-3" style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)' }}>
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  disabled={chatLoading}
                  className="flex-grow rounded-xl py-2.5 px-4 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)' }}
                  placeholder={chatLoading ? 'Thinking…' : 'Ask anything…'}
                />
                <button
                  type="submit"
                  disabled={chatLoading || !chatInput.trim()}
                  className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                >
                  {chatLoading ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 md:py-32 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                Platform
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-5">
                The Complete{' '}
                <span style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  AI Business Suite
                </span>
              </h2>
              <p className="text-base md:text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Everything you need to automate operations with intelligent voice AI — all in one platform.
              </p>
            </div>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-20">
              {[
                {
                  icon: (
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ),
                  title: 'Instant Setup',
                  body: 'Launch your AI voice agent in minutes — no engineering team required.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  ),
                  title: 'Real-Time Analytics',
                  body: 'Track every call with detailed logs, duration, cost, and success metrics.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                  title: '24/7 Availability',
                  body: 'Your AI never sleeps. Answer calls, book appointments, and qualify leads around the clock.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 00-1.066 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  title: 'Full Customization',
                  body: 'Configure voice, personality, tone, and knowledge base to match your brand.',
                },
              ].map(({ icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl p-6 flex flex-col gap-4 group transition-all duration-300 hover:scale-[1.02]"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(139,92,246,0.12)', border: '0.5px solid rgba(139,92,246,0.2)' }}>
                    {icon}
                  </div>
                  <h3 className="text-base font-semibold text-white">{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{body}</p>
                </div>
              ))}
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-6 mb-20 max-w-2xl mx-auto text-center">
              {[
                { num: '500+', label: 'Businesses Automated' },
                { num: '1M+', label: 'Calls Handled' },
                { num: '99.9%', label: 'Uptime SLA' },
              ].map(({ num, label }) => (
                <div key={label}>
                  <p className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">{num}</p>
                  <p className="mt-1 text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center rounded-2xl py-14 px-8" style={{ background: 'rgba(139,92,246,0.06)', border: '0.5px solid rgba(139,92,246,0.2)' }}>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 tracking-tight">
                Ready to Transform Your Business?
              </h3>
              <p className="mb-8 max-w-lg mx-auto text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Join businesses already using Appify.AI to automate operations and grow revenue.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <a
                  href={getAppUrl('/register')}
                  className="px-8 py-3.5 text-sm font-semibold text-white rounded-xl transition hover:opacity-90 hover:scale-105 inline-block text-center"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                >
                  Create your Agent
                </a>
                <button
                  type="button"
                  onClick={() => setShowDemoModal(true)}
                  className="px-8 py-3.5 text-sm font-semibold text-white rounded-xl transition hover:bg-white/5"
                  style={{ border: '0.5px solid rgba(255,255,255,0.2)' }}
                >
                  Book a Demo
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations */}
        <section className="py-20 md:py-28 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                Integrations
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-3">
                Works with your stack
              </h2>
              <p className="text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Connect with 40+ tools — CRMs, calendars, and cloud platforms.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-2xl p-8" style={{ background: 'rgba(255,255,255,0.02)', border: '0.5px solid rgba(255,255,255,0.07)' }}>
              <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #07070f, transparent)' }} />
              <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #07070f, transparent)' }} />

              <div className="flex animate-scroll space-x-8">
                {[0, 1].map((loop) => (
                  <div key={loop} className="flex space-x-12 flex-shrink-0 items-center">
                    {[
                      /* simple-icons@13.21.0 — Azure slug removed in newer majors; keep on 11.x */
                      {
                        name: 'OpenAI',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/openai.svg',
                        light: true,
                      },
                      {
                        name: 'Google Calendar',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/googlecalendar.svg',
                      },
                      {
                        name: 'HubSpot',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/hubspot.svg',
                      },
                      {
                        name: 'Salesforce',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/salesforce.svg',
                      },
                      {
                        name: 'Zoom',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/zoom.svg',
                      },
                      {
                        name: 'Notion',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/notion.svg',
                        light: true,
                      },
                      {
                        name: 'Zapier',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/zapier.svg',
                      },
                      {
                        name: 'Microsoft Azure',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@11.14.0/icons/microsoftazure.svg',
                      },
                      {
                        name: 'AWS',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/amazonwebservices.svg',
                      },
                      {
                        name: 'Google Cloud',
                        src: 'https://cdn.jsdelivr.net/npm/simple-icons@13.21.0/icons/googlecloud.svg',
                      },
                    ].map(({ name, src, light }) => (
                      <div
                        key={`${loop}-${name}`}
                        className="flex flex-col items-center justify-center min-w-[88px] gap-2.5 hover:scale-105 transition-transform duration-300"
                      >
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center p-2.5"
                          style={light
                            ? { backgroundColor: 'rgba(255,255,255,0.92)' }
                            : { backgroundColor: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.1)' }
                          }
                        >
                          <img
                            src={src}
                            alt=""
                            width={40}
                            height={40}
                            className={`w-10 h-10 object-contain ${light ? '' : 'brightness-0 invert opacity-80'}`}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <span className="text-xs text-center leading-tight max-w-[88px]" style={{ color: 'rgba(255,255,255,0.4)' }}>{name}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* Trusted by Leading Brands Section - COMMENTED OUT FOR NOW */}
        {/* 
        <section className="py-14 md:py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
                Trusted by <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Leading Brands</span>
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Join thousands of businesses worldwide who trust Appify.AI to automate their operations and grow their revenue.
              </p>
            </div>
            
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-gray-800/20 to-gray-700/20 border border-gray-600/30 p-8">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-800/20 to-transparent z-10"></div>
              <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-800/20 to-transparent z-10"></div>
              
              <div className="flex animate-scroll space-x-8">
                <div className="flex space-x-8 flex-shrink-0">
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/microsoft-logo.svg" 
                      alt="Microsoft" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Microsoft</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/google-logo.svg" 
                      alt="Google" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Google</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/amazon-logo.svg" 
                      alt="Amazon" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Amazon</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/salesforce-logo.svg" 
                      alt="Salesforce" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Salesforce</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/hubspot-logo.svg" 
                      alt="HubSpot" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">HubSpot</div>
                  </div>
                </div>
                <div className="flex space-x-8 flex-shrink-0">
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/microsoft-logo.svg" 
                      alt="Microsoft" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Microsoft</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/google-logo.svg" 
                      alt="Google" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Google</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/amazon-logo.svg" 
                      alt="Amazon" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Amazon</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/salesforce-logo.svg" 
                      alt="Salesforce" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">Salesforce</div>
                  </div>
                  <div className="bg-white/90 p-6 rounded-xl border border-gray-200 flex items-center justify-center min-w-[200px] h-20 hover:shadow-lg transition-all duration-300 hover:scale-105">
                    <img 
                      src="/logos/hubspot-logo.svg" 
                      alt="HubSpot" 
                      className="max-h-12 w-auto object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden text-gray-600 font-semibold text-lg">HubSpot</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        */}

        {/* FAQ Section */}
        <section className="py-20 md:py-28 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-14">
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                FAQ
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Frequently Asked Questions</h2>
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <FAQItem
                question="How long does it take to get started?"
                answer="Start free with our demo to build and test your AI agent instantly. When you go live, Growth and Pro plans include professional setup — Pro/Business includes priority deployment for faster go-live."
              />
              <FAQItem
                question="Is there customer support available?"
                answer="Yes! We support you from demo through launch. Growth includes standard onboarding and setup support; Pro / Business adds priority setup and is built for teams that need hands-on guidance."
              />
              <FAQItem
                question="How much does your platform cost?"
                answer="The Free Demo costs nothing — explore and customise before you commit. Paid plans start from $249/month (Growth) and from $649/month (Pro / Business), plus one-time setup. Usage is billed per minute at the rates shown on each plan."
              />
              <FAQItem
                question="Is my data secure on your platform?"
                answer="We take security seriously: encryption in transit, access controls, and careful use of trusted infrastructure providers. Specific certifications and subprocessors depend on your plan — ask us for details. We do not sell your personal data."
              />
              <FAQItem
                question="How does Appify.AI compare to other tools?"
                answer="Appify.AI offers the most comprehensive voice AI solution with real-time analytics, custom AI workflows, and seamless integrations. Unlike basic chatbots, our AI can handle complex conversations, book appointments, and qualify leads — like having a capable assistant on every call."
              />
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 md:py-28 px-4 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="mb-14">
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                Pricing
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Simple, transparent pricing</h2>
              <p className="mt-3 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Built around your business. No surprises.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-left">
              {tiers.map((tier) => (
                <div
                  key={tier.name}
                  className="rounded-2xl p-7 flex flex-col relative transition-all duration-300 hover:scale-[1.01]"
                  style={tier.popular ? {
                    background: 'rgba(139,92,246,0.07)',
                    border: '1px solid rgba(139,92,246,0.5)',
                    boxShadow: '0 0 40px rgba(139,92,246,0.12)',
                  } : {
                    background: 'rgba(255,255,255,0.03)',
                    border: '0.5px solid rgba(255,255,255,0.08)',
                  }}
                >
                  {tier.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="text-xs font-semibold text-white px-3 py-1 rounded-full" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-white">{tier.name}</h3>
                  <p className="mt-4 text-4xl font-extrabold text-white tracking-tight">{tier.price}</p>
                  {tier.setupLines.map((line) => (
                    <p key={line} className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{line}</p>
                  ))}
                  <div className="mt-7 space-y-5 flex-grow" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {tier.blocks.map((block, blockIndex) => {
                      if (block.type === 'list') {
                        return (
                          <ul key={blockIndex} className="space-y-2.5">
                            {block.items.map((item, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="mt-0.5 shrink-0 text-purple-400">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <span className="text-sm leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      if (block.type === 'section') {
                        return (
                          <div key={blockIndex}>
                            <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">{block.title}</p>
                            <ul className="space-y-2.5">
                              {block.items.map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                  <span className="mt-0.5 shrink-0 text-purple-400">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <span className="text-sm leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      }
                      if (block.type === 'tagline') {
                        return (
                          <div key={blockIndex} className="rounded-xl p-4" style={{ backgroundColor: 'rgba(139,92,246,0.08)', border: '0.5px solid rgba(139,92,246,0.2)' }}>
                            <p className="text-sm leading-relaxed text-purple-200">{block.text}</p>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                  {tier.cta.action === 'register' ? (
                    <a
                      href={getAppUrl('/register')}
                      className="mt-8 w-full block text-center px-6 py-3 text-sm font-semibold text-white rounded-xl transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                    >
                      {tier.cta.label}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowDemoModal(true)}
                      className="mt-8 w-full block text-center px-6 py-3 text-sm font-semibold text-white rounded-xl transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                    >
                      {tier.cta.label}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-12 max-w-2xl mx-auto text-sm font-medium" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Most businesses save 60–80% compared to hiring a full-time employee for phone and customer-facing work.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 md:py-28 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-lg mx-auto">
            <div className="text-center mb-12">
              <div className="mb-5 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase" style={{ border: '1px solid rgba(139,92,246,0.3)', color: '#C084FC', backgroundColor: 'rgba(139,92,246,0.08)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block"></span>
                Contact
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Ready to Get Started?</h2>
              <p className="mt-3 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Book a demo or drop us a line. We&apos;d love to hear from you.</p>
            </div>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="block w-full rounded-xl py-3 px-4 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)' }}
              />
              <input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-xl py-3 px-4 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)' }}
              />
              <textarea
                rows="4"
                placeholder="Describe your business…"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="block w-full rounded-xl py-3 px-4 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500/50 transition resize-none"
                style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)' }}
              ></textarea>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  disabled={contactSubmitting}
                  className="w-full sm:w-1/2 py-3 text-sm font-semibold text-white rounded-xl transition hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ border: '0.5px solid rgba(255,255,255,0.15)' }}
                >
                  {contactSubmitting ? 'Sending…' : 'Send Message'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDemoModal(true)}
                  className="w-full sm:w-1/2 py-3 text-sm font-semibold text-white rounded-xl transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                >
                  Book a Demo
                </button>
              </div>
              {contactError && <p className="text-amber-400 text-xs">{contactError}</p>}
              {contactSuccess && !contactError && (
                <p className="text-green-400 text-xs">Thanks! We'll be in touch shortly.</p>
              )}
              {contactSuccess && contactError && (
                <p className="text-green-400 text-xs">If your email app opened, send the draft and we'll reply from there.</p>
              )}
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-14 px-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-12">
              {/* Brand */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="/screenshots/appymascot.png.png"
                    alt="Appy"
                    className="w-10 h-10 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <div className="hidden w-10 h-10 rounded-full items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>A</div>
                  <span className="text-base font-bold text-white">Appify.AI</span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Intelligent AI voice agents that never sleep. Answer calls, book appointments, and handle inquiries 24/7.
                </p>
              </div>

              {/* Links */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>Product</p>
                  <div className="space-y-3">
                    <a href="#features" className="block text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>Features</a>
                    <a href="#pricing" className="block text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>Pricing</a>
                    <a href="#contact" className="block text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>Contact</a>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>Legal</p>
                  <div className="space-y-3">
                    <Link to="/terms" className="block text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>Terms of Service</Link>
                    <Link to="/privacy" className="block text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>Privacy Policy</Link>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.3)' }}>Contact</p>
                <div className="space-y-3">
                  <a href="mailto:admin@appifyai.com" className="flex items-center gap-2.5 text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    admin@appifyai.com
                  </a>
                  <a href="tel:+15419070061" className="flex items-center gap-2.5 text-sm transition hover:text-white" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    +1 (541) 907 0061
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: '0.5px solid rgba(255,255,255,0.07)' }}>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>&copy; 2026 Appify.AI. All rights reserved.</p>
              <div className="flex items-center gap-5">
                <Link to="/terms" className="text-xs transition hover:text-white" style={{ color: 'rgba(255,255,255,0.35)' }}>Terms</Link>
                <Link to="/privacy" className="text-xs transition hover:text-white" style={{ color: 'rgba(255,255,255,0.35)' }}>Privacy</Link>
              </div>
            </div>
          </div>
        </footer>
        </div>
      </div>
      
      {/* Demo Booking Modal */}
      <DemoBookingModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)} 
      />
    </div>
  );
} 